import React, {useEffect, useMemo} from 'react';
import ReportUtils from "../../../../../utils/report-utils.js";
import {translate} from "../../../../../components/translate/translate.jsx";
import {useTableSortHeader} from "../../../../../components/reports/table-sort-header/table-sort-header.jsx";
import {navigateHook} from "../../../../../hooks/use-navigate.js";
import Button from "../../../../../components/button/button.jsx";
import STimeline from "../../../../../components/icons/timeline.jsx";

const actionsTranslate = {
    "APP.DELETE": "Excluído pelo app",
    "APPROVED": "Aprovado",
    "REJECTED": "Reprovado",
    "REVIEW": "Revisado",
    "CREATE": "Criado",
    "DATA.CREATE": "Criação de dados",
    "DATA.UPDATE": "Corrigido",
    "DATA.RECTIFY": "Retificado",
    "DATA.ADDOTHERDOCUMENT": "Documento adicionado",
    "DRAFT": "Em preenchimento",
    "FILE.CREATE": "Arquivo criado",
    "INVALIDATE": "Invalidado",
};

const translateStatusActions = (value) => {
    if (!value) return "";

    const [status, action] = value.split('-').map(item => item.trim());

    const translatedStatus = translate(status) ?? status;
    const translatedAction = actionsTranslate[action?.toUpperCase()] ?? action;

    if (!action) return translatedStatus;
    if (!status) return translatedAction;

    return `${translatedStatus} - ${translatedAction}`;
};

const ReportContent = ({ data, boName, tableColumns }) => {
    useEffect(() => {
        ReportUtils.createExportableTable(null, null, `Auditoria do ${boName}`, "bo-audit-data");
    }, [data, boName, tableColumns]);

    const sortValueExtraction = useMemo(() => ({
        status_value: (row) => translate(row.status_value)
    }), []);

    const { order, handleSort, sortedData, getIcon } = useTableSortHeader(data, sortValueExtraction);
    const navigate = navigateHook();
    const getStatusClass = (value) => {
        const action = value?.split('-').pop().trim().toLowerCase();
        if (action === 'approved') return 'text-green-600! font-bold!';
        if (action === 'rejected') return 'text-red-600! font-bold!';
        return '';
    };
    const columns = [
        {id: "bogcm_id", label: boName, className: "w-[9%]", sortField: "bogcm_id", renderCell: element => element?.bogcm_id ? <a className="inner-link" onClick={() => navigate(`/bo/redirect/${element.bogcm_id.replace('/', '-')}`, true)}>{element.bogcm_id}</a> : "Sem talão"},
        {id: "time_action", label: "Data/Hora", className: "w-[12%]", sortField: "time_action", renderCell: element => element?.time_action ?? ""},
        {id: "validation_namewar", label: "Agente", className: "w-[12%]", sortField: "validation_namewar", renderCell: element => ReportUtils.formatText(element?.validation_namewar) ?? ""},
        {id: "status_value", label: "Status", className: "w-[10%]", sortField: "status_value", renderCell: element => <span className={getStatusClass(element?.status_value)}>{translateStatusActions(element?.status_value)}</span>},
        {id: "rejected_justification", label: "Motivo da reprovação", className: "w-[16%]", sortField: "rejected_justification", renderCell: element => element?.rejected_justification ?? ""},
        {id: "status_observation", label: "Observação", className: "w-[19%]", sortField: "status_observation", renderCell: element => element?.status_observation ?? ""},
        {id: "timeline", label: "Linha do tempo", className: "w-[11%] print:hidden! export-ignore text-center", renderCell: element => <section className="flex flex-row justify-center w-full!"><Button title={`Linha do tempo do ${boName}${element?.bogcm_id ? ` - ${element.bogcm_id}` : ''}`} className="print:hidden px-2!" onClick={() => navigate(`/reports/bo_timeline?id=${element?.bogcm_id.replace('/', '-')}`, true)} disabled={!element?.bogcm_id}><STimeline/></Button></section>},
    ];
    const visibleColumns = columns.filter(column => tableColumns?.[column.id]?.visible !== false);
    const printableColumns = visibleColumns.filter(column => tableColumns?.[column.id]?.printIgnore !== true);
    const onlyPrintIgnoredColumns = visibleColumns.length > 0 && printableColumns.length === 0;

    return (
        <>
            {visibleColumns.length === 0 ? (
                <div className="alert alert-info p-2 h6 text-center mt-[20px]!">Todas as colunas foram ocultadas.</div>
            ) : (
                <>
                    <table id="bo-audit-data" className={`table report-table w-full! ${onlyPrintIgnoredColumns ? "print:hidden" : ""}`} sheet-name={`Auditoria do ${boName}`}>
                        <thead className="thead-background print:table-row-group!">
                            <tr className="bg-[#f6f6f6] text-left">
                                {visibleColumns.map(column => <th key={column.id} onClick={column.sortField ? () => handleSort(column.sortField) : undefined} className={column.className}>{tableColumns?.[column.id]?.label ?? column.label} {column.sortField && getIcon(order, column.sortField)}</th>)}
                            </tr>
                        </thead>
                        <tbody className="bg-white">
                            {sortedData.map((element, index) => <tr key={index} className="table-break-word">{visibleColumns.map(column => <td key={column.id} className={column.className}>{column.renderCell(element)}</td>)}</tr>)}
                        </tbody>
                    </table>
                    {onlyPrintIgnoredColumns && <div className="hidden print:block alert alert-info p-2 h6 text-center mt-[20px]!">Todas as colunas foram ocultadas.</div>}
                </>
            )}

            <div id="chart-tables"/>
        </>
    );
};

export default ReportContent;